/* ============================================================
   Bundle Widget JS — Hooks into the theme's existing
   Add to Cart / Buy It Now buttons instead of rendering its own.
   File: assets/bundle-widget.js
============================================================ */

(function () {
  'use strict';

  // ---------- Money formatting ----------
  function formatMoney(cents, currencySymbol) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    const value = (cents / 100).toFixed(2);
    const display = value.endsWith('.00') ? value.slice(0, -3) : value;
    const withCommas = display.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${currencySymbol || '₹'}${withCommas}`;
  }

  // ---------- Selectors for the theme's buttons ----------
  const ADD_TO_CART_SELECTORS = [
    'form[action*="/cart/add"] button[type="submit"]',
    '.product-form__buttons button[type="submit"]',
    '.product-form__cart-submit',
    'button[name="add"]',
    '.btn-addtocart',
    '.add-to-cart-button'
  ];

  const BUY_NOW_SELECTORS = [
    '.shopify-payment-button button',
    '.shopify-payment-button__button',
    '.dynamic-checkout button',
    '.btn-buynow',
    '.buy-it-now-button'
  ];

  // ---------- Helpers ----------
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[c]));
  }

  /**
   * When a chip is clicked in the widget, click the matching radio input in
   * the theme's hidden variant picker. The theme's existing JS handlers then
   * fire — gallery filter, variant URL update, inventory checks, etc.
   */
  function syncThemeVariantPicker(group, chip) {
    const optionName = (group.dataset.optionName || '').trim();
    const value = chip.dataset.optionValue;
    if (!value) return;

    // Only sync gallery on COLOR option clicks
    if (!/colou?r/i.test(optionName)) return;

    const normalize = (s) => (s || '').toLowerCase().trim().replace(/[\s_-]+/g, '');
    const targetValue = normalize(value);

    // Find the matching radio in the MAIN product info (not recommendations)
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const candidates = productInfo.querySelectorAll('input[type="radio"]');
    let matchedRadio = null;
    let matchedLabel = null;

    for (const radio of candidates) {
      if (radio.classList.contains('bundle-pack__radio')) continue;
      const radioName = (radio.name || '').toLowerCase();
      const radioValue = radio.value || '';

      if (!/colou?r/.test(radioName)) continue;
      if (normalize(radioValue) !== targetValue) continue;

      matchedRadio = radio;
      matchedLabel = document.querySelector(`label[for="${radio.id}"]`);
      break;
    }

    if (!matchedRadio) return;
    if (matchedRadio.checked) return;

    const scrollY = window.scrollY;
    matchedRadio.checked = true;
    const clickTarget = matchedLabel || matchedRadio;
    clickTarget.click();

    const restore = () => window.scrollTo(0, scrollY);
    restore();
    [16, 50, 100, 250, 500, 1000].forEach(d => setTimeout(restore, d));
  }

  function buildItemsHtml(qty, productData) {
    let html = '';
    for (let i = 1; i <= qty; i++) {
      if (i > 1) html += '<div class="bundle-pack__divider"></div>';
      html += buildItemHtml(i, productData);
    }
    return html;
  }

  function buildItemHtml(index, productData) {
    let html = `<div class="bundle-item" data-bundle-item data-item-index="${index}">`;
    productData.options.forEach(opt => {
      const isColor = /colou?r/i.test(opt.name);
      html += `<div class="bundle-item__group" data-option-group data-option-name="${escapeHtml(opt.name)}" data-option-position="${opt.position}">`;
      html += `<span class="bundle-item__group-label">${escapeHtml(opt.name)}</span>`;
      html += `<div class="bundle-item__choices">`;
      opt.values.forEach(val => {
        const cssColor = val.toLowerCase().replace(/\s+/g, '');
        const swatch = isColor ? `<span class="bundle-chip__swatch" style="background-color:${cssColor};"></span>` : '';
        html += `<button type="button" class="bundle-chip${isColor ? ' bundle-chip--color' : ''}" data-option-value="${escapeHtml(val)}" data-option-position="${opt.position}" aria-label="${escapeHtml(opt.name)}: ${escapeHtml(val)}">${swatch}<span class="bundle-chip__text">${escapeHtml(val)}</span></button>`;
      });
      html += `</div></div>`;
    });
    html += `</div>`;
    return html;
  }

  function getSelectedOptionsForItem(item) {
    const groups = item.querySelectorAll('[data-option-group]');
    const opts = [];
    groups.forEach(group => {
      const activeChip = group.querySelector('.bundle-chip.is-active');
      opts.push(activeChip ? activeChip.dataset.optionValue : null);
    });
    return opts;
  }

  function findVariant(selectedOpts, productData) {
    return productData.variants.find(v =>
      selectedOpts.every((val, i) => {
        if (val === null) return true;
        return v[`option${i + 1}`] === val;
      })
    );
  }

  function updateAvailabilityForItem(item, productData) {
    const groups = item.querySelectorAll('[data-option-group]');
    groups.forEach((group, gIdx) => {
      const chips = group.querySelectorAll('.bundle-chip');
      chips.forEach(chip => {
        const trial = getSelectedOptionsForItem(item);
        trial[gIdx] = chip.dataset.optionValue;
        const variant = findVariant(trial, productData);
        if (!variant || !variant.available) chip.classList.add('is-unavailable');
        else chip.classList.remove('is-unavailable');
      });
    });
  }

  // ---------- Init one widget ----------
  function initBundleWidget(root) {
    const productId = root.dataset.productId;
    const dataScript = document.querySelector(`script[data-product-json="${productId}"]`);
    if (!dataScript) {
      console.warn('[bundle-widget] product JSON not found for', productId);
      return;
    }

    let productData;
    try {
      productData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[bundle-widget] failed to parse product JSON', e);
      return;
    }

    let tiers;
    try {
      tiers = JSON.parse(root.dataset.tiers || '[]');
    } catch (e) {
      console.error('[bundle-widget] failed to parse tiers config', e);
      return;
    }
    if (!Array.isArray(tiers) || tiers.length === 0) {
      root.style.display = 'none';
      return;
    }

    tiers.sort((a, b) => (a.qty || 1) - (b.qty || 1));

    const currency = productData.currency || '₹';
    const basePrice = productData.variants[0] ? productData.variants[0].price : 0;
    const packsContainer = root.querySelector('[data-packs-container]');
    const errorEl = root.querySelector('[data-error]');

    // ---------- Render tiers ----------
    let defaultSelectedIdx = tiers.findIndex(t => t.featured);
    if (defaultSelectedIdx < 0) defaultSelectedIdx = tiers.length > 1 ? 1 : 0;

    tiers.forEach((tier, idx) => {
      const qty = tier.qty || 1;
      const label = tier.label || `Pack of ${qty}`;
      const discount = (tier.discount || 0) * 100;
      const fullPrice = basePrice * qty;
      const finalPrice = Math.max(0, fullPrice - discount);

      const packEl = document.createElement('label');
      packEl.className = 'bundle-pack' + (tier.featured ? ' bundle-pack--featured' : '');
      packEl.dataset.pack = qty;
      packEl.dataset.tierIndex = idx;

      let badgeHtml = tier.badge ? `<span class="bundle-pack__badge">${escapeHtml(tier.badge)}</span>` : '';
      let savingsHtml = discount > 0 ? `<span class="bundle-pack__savings">You save ${formatMoney(discount, currency)}</span>` : '';
      let priceHtml = `<span class="bundle-pack__price">${formatMoney(finalPrice, currency)}</span>`;
      if (discount > 0) {
        priceHtml = `<span class="bundle-pack__price-strike">${formatMoney(fullPrice, currency)}</span>${priceHtml}`;
      }

      packEl.innerHTML = `
        ${badgeHtml}
        <input type="radio" name="bundle-pack-${productData.id}" value="${qty}" class="bundle-pack__radio" data-pack-radio${idx === defaultSelectedIdx ? ' checked' : ''}>
        <div class="bundle-pack__head">
          <div class="bundle-pack__head-left">
            <span class="bundle-pack__dot"></span>
            <div class="bundle-pack__labels">
              <span class="bundle-pack__label">${escapeHtml(label)}</span>
              ${savingsHtml}
            </div>
          </div>
          <div class="bundle-pack__price-wrap">${priceHtml}</div>
        </div>
        <div class="bundle-pack__body">
          ${buildItemsHtml(qty, productData)}
        </div>
      `;
      packsContainer.appendChild(packEl);
    });

    // ---------- Pack click (With Accordion Fix) ----------
    const packs = root.querySelectorAll('.bundle-pack');

    function selectPack(packEl) {
      const isAlreadySelected = packEl.classList.contains('is-selected');

      packs.forEach(p => {
        p.classList.remove('is-selected');
        const radio = p.querySelector('[data-pack-radio]');
        if (radio) radio.checked = false;
      });

      if (!isAlreadySelected) {
        packEl.classList.add('is-selected');
        const radio = packEl.querySelector('[data-pack-radio]');
        if (radio) radio.checked = true;
      }
    }

    packs.forEach(pack => {
      pack.addEventListener('click', (e) => {
        if (e.target.className && String(e.target.className).includes('bundle-pack__radio')) {
          return;
        }
        if (e.target.closest('.bundle-chip')) return;
        selectPack(pack);
      });
    });

    const checkedPack = root.querySelector('[data-pack-radio]:checked');
    if (checkedPack) {
      checkedPack.closest('.bundle-pack').classList.add('is-selected');
    }

    // ---------- Chip click ----------
    const items = root.querySelectorAll('[data-bundle-item]');
    items.forEach(item => {
      const groups = item.querySelectorAll('[data-option-group]');
      groups.forEach(group => {
        const chips = group.querySelectorAll('.bundle-chip');
        const firstAvailable = Array.from(chips).find(c => !c.classList.contains('is-unavailable'));
        if (firstAvailable) firstAvailable.classList.add('is-active');
        chips.forEach(chip => {
          chip.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (chip.classList.contains('is-unavailable')) return;
            chips.forEach(c => c.classList.remove('is-active'));
            chip.classList.add('is-active');
            updateAvailabilityForItem(item, productData);
            syncThemeVariantPicker(group, chip);
          });
        });
      });
      updateAvailabilityForItem(item, productData);
    });

    // ---------- Sync gallery on initial page load ----------
    setTimeout(() => {
      const activePack = root.querySelector('.bundle-pack.is-selected');
      if (!activePack) return;
      const firstItem = activePack.querySelector('[data-bundle-item]');
      if (!firstItem) return;
      const colorGroup = Array.from(firstItem.querySelectorAll('[data-option-group]'))
        .find(g => /colou?r/i.test(g.dataset.optionName || ''));
      if (!colorGroup) return;
      const activeChip = colorGroup.querySelector('.bundle-chip.is-active');
      if (activeChip) {
        syncThemeVariantPicker(colorGroup, activeChip);
      }
    }, 800);

    // ---------- Add to cart logic ----------
    function getActivePack() { return root.querySelector('.bundle-pack.is-selected'); }

    function buildCartItems() {
      const activePack = getActivePack();
      if (!activePack) throw new Error('Please select a pack bundle option first.');
      const packItems = activePack.querySelectorAll('[data-bundle-item]');
      const lineItems = [];
      for (const item of packItems) {
        const opts = getSelectedOptionsForItem(item);
        if (opts.some(o => o === null)) {
          throw new Error('Please choose every option for each item.');
        }
        const variant = findVariant(opts, productData);
        if (!variant) throw new Error(`No variant matches ${opts.join(' / ')}`);
        if (!variant.available) throw new Error(`${variant.title} is sold out.`);
        lineItems.push({ id: variant.id, quantity: 1 });
      }
      const merged = {};
      lineItems.forEach(li => { merged[li.id] = (merged[li.id] || 0) + li.quantity; });
      return Object.entries(merged).map(([id, quantity]) => ({ id: Number(id), quantity }));
    }

    function showError(msg) {
      errorEl.textContent = msg;
      errorEl.hidden = false;
      setTimeout(() => { errorEl.hidden = true; }, 4000);
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    async function addBundleToCart(redirectToCheckout, originalBtn) {
      errorEl.hidden = true;
      let items;
      try { items = buildCartItems(); }
      catch (e) { showError(e.message); return false; }

      const originalText = originalBtn ? originalBtn.textContent : '';
      if (originalBtn && !originalBtn.dataset._bwOrig) {
        originalBtn.dataset._bwOrig = originalText;
      }
      if (originalBtn) {
        originalBtn.disabled = true;
        originalBtn.textContent = 'ADDING…';
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

        if (redirectToCheckout) {
          window.location.href = '/checkout';
        } else {
          document.dispatchEvent(new CustomEvent('cart:updated'));
          window.location.href = '/cart';
        }
        return true;
      } catch (e) {
        showError(e.message);
        if (originalBtn) {
          originalBtn.disabled = false;
          originalBtn.textContent = originalBtn.dataset._bwOrig || originalText;
        }
        return false;
      }
    }

    // ---------- Hook the theme's existing buttons ----------
    function isAddToCartButton(el) {
      return ADD_TO_CART_SELECTORS.some(sel => el.matches && el.matches(sel));
    }
    function isBuyNowButton(el) {
      return BUY_NOW_SELECTORS.some(sel => el.matches && el.matches(sel));
    }

    // THE KEY GUARD: only intercept buttons that are inside the same
    // product-info container as this bundle widget root.
    // This stops the product-page listener from firing when buttons are
    // clicked inside the catalog quick-add modal (which is a separate
    // product-info injected into .quick-view).
    function isSameProductContext(el) {
      const rootProductInfo = root.closest('product-info');
      if (!rootProductInfo) return false;
      return rootProductInfo.contains(el);
    }

    // Capture-phase click handler intercepts BEFORE the theme's own handlers run
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('button, a');
      if (!btn) return;

      // Only intercept buttons inside the same product-info as this widget
      if (!isSameProductContext(btn)) return;

      if (isAddToCartButton(btn)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        addBundleToCart(false, btn);
        return;
      }

      if (isBuyNowButton(btn)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        addBundleToCart(true, btn);
        return;
      }
    }, true);

    // Also intercept form submit (Enter key, theme's JS-driven submits)
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form || !form.matches) return;
      if (!form.matches('form[action*="/cart/add"]')) return;

      // Only intercept forms inside the same product-info as this widget
      if (!isSameProductContext(form)) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      const submitBtn = form.querySelector('button[type="submit"]');
      addBundleToCart(false, submitBtn);
    }, true);
  }

const initializedWidgets = new WeakSet();

function init() {
  document.querySelectorAll('[data-bundle-widget]').forEach(root => {
    if (initializedWidgets.has(root)) return;
    initializedWidgets.add(root);
    initBundleWidget(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Watch for quickview / dynamically injected bundle widgets
const observer = new MutationObserver(function() {
  document.querySelectorAll('[data-bundle-widget]').forEach(root => {
    if (initializedWidgets.has(root)) return;
    initializedWidgets.add(root);
    initBundleWidget(root);
  });
});

observer.observe(document.body, { childList: true, subtree: true });
})();